<template>
    <div class="card mb-3" :class="{deleted: user.deleted_at}">
        <div class="card-body">
            <div class="row">
                <div class="col-md-3">
                    <h4>
                        <img v-if="user.profile_url" :src="user.profile_url" class="user-icon">
                        <ruby>{{ user.name }}<rt>{{ user.kana }}</rt></ruby>
                        <span class="badge badge-danger badge-pill" v-if="user.is_root">管理者</span>
                        <span class="badge badge-success badge-pill" v-if="user.is_guest">お試し</span>
                        <span class="badge badge-primary badge-pill" v-if="user.is_online">
                            <i class="fas fa-wifi"></i>
                        </span>
                        <div class="small text-muted" v-if="user.inviter">
                            招待者：{{user.inviter.name}}
                        </div>
                    </h4>
                    <div class="small">
                        {{ user.email }}
                        <span v-if="user.email_verified_at" class="text-success"><i class="fas fa-check-circle"></i></span>
                        <span v-else class="text-danger"><i class="fas fa-ban"></i></span>
                    </div>
                    <div class="small">
                        <span v-if="user.is_google" class="text-danger">Google連携</span>
                        <span v-if="user.has_password" class="text-muted">パスワードログイン</span>
                    </div>
                    <div>
                        <a :href="`https://x.com/${user.twitter}`" target="_blank" v-if="user.twitter" class="btn btn-twitter btn-sm">
                            <i class="fab fa-twitter"></i>
                        </a>
                        <a :href="`https://bsky.app/profile/${user.bluesky}`" target="_blank" v-if="user.bluesky" class="btn btn-primary btn-sm">
                            <img src="/img/bluesky-white.svg" class="bluesky-icon">
                        </a>
                    </div>
                </div>
                <div class="col-md-2">
                    <div>
                        <span class="badge" :class="{
                            'badge-secondary': forPortalBackgrounds(user.backgrounds) == 0,
                            'badge-success': forPortalBackgrounds(user.backgrounds) > 0
                        }">
                            ポータル: {{forPortalBackgrounds(user.backgrounds)}}
                        </span>
                    </div>
                    <div>
                        <span class="badge" :class="{
                            'badge-secondary': forBookshelfBackgrounds(user.backgrounds) == 0,
                            'badge-success': forBookshelfBackgrounds(user.backgrounds) > 0
                        }">
                            本棚: {{forBookshelfBackgrounds(user.backgrounds)}}
                        </span>
                    </div>
                    <div>
                        <span class="badge" :class="{
                            'badge-secondary': forBookBackgrounds(user.backgrounds) == 0,
                            'badge-success': forBookBackgrounds(user.backgrounds) > 0
                        }">
                            小説: {{forBookBackgrounds(user.backgrounds)}}
                        </span>
                    </div>
                    <div>
                        <span class="badge" :class="{
                            'badge-secondary': forEpisodeBackgrounds(user.backgrounds) == 0,
                            'badge-success': forEpisodeBackgrounds(user.backgrounds) > 0
                        }">
                            本文: {{forEpisodeBackgrounds(user.backgrounds)}}
                        </span>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="fs-big">
                        <a v-if="user.subdomain" :href="authorUrl(user)" target="_blank">{{user.subdomain}}</a>
                        <span v-else>-</span>
                        <span v-if="user.tracking_id">
                            <i class="fas fa-chart-bar"></i>
                        </span>
                    </div>
                    <div v-if="user.subdomain" v-html="$filters.ruby(user.bookshelf_name)">
                    </div>
                    <div>
                        <span v-if="user.rating" class="badge" :class="{
                            'badge-warning': user.rating.color === 'warning',
                            'badge-danger': user.rating.color === 'danger',
                            'badge-dark': user.rating.color === 'dark',
                        }">{{user.rating.rating_name}}</span>
                        <span v-else class="badge badge-secondary">無し</span>
                    </div>
                    <div>
                        <span class="badge" :class="{
                            'badge-white': user.background_color == 'white',
                            'badge-black': user.background_color == 'black',
                            'badge-zouge': user.background_color == 'zouge',
                            'badge-sakura': user.background_color == 'sakura',
                            'badge-dreamy': user.background_color == 'dreamy',
                            'badge-forest': user.background_color == 'forest',
                            'badge-cosmo': user.background_color == 'cosmo',
                            'badge-metal': user.background_color == 'metal',
                            'badge-doom': user.background_color == 'doom',
                            'badge-rome': user.background_color == 'rome',
                            'badge-jupiter': user.background_color == 'jupiter',
                            'badge-space': user.background_color == 'space',
                            'badge-small': user.font_size == 1,
                            'badge-medium': user.font_size == 2,
                            'badge-large': user.font_size == 3,
                            'badge-xlarge': user.font_size == 4,
                            'badge-huge': user.font_size == 5,
                            'mincho': user.font == 'mincho',
                            'gothic': user.font == 'gothic',
                        }">
                            <span v-if="user.font_size == 1">小</span>
                            <span v-else-if="user.font_size == 2">中</span>
                            <span v-else-if="user.font_size == 3">大</span>
                            <span v-else-if="user.font_size == 4">特大</span>
                            <span v-else-if="user.font_size == 5">最大</span>
                            |
                            <span v-if="user.font == 'mincho'">明</span>
                            <span v-if="user.font == 'gothic'">ゴ</span>
                            <span v-if="user.font == 'ud'">UD</span>
                            <span v-if="user.font == 'kaisei'">解</span>
                            |
                            <span v-if="user.is_horizontal == true">横</span>
                            <span v-else>縦</span>
                        </span>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="mr-2" :class="{
                        'text-muted': user.books_count == 0,
                        'text-danger': isOver(user.books_count, user.novel_limit),
                        'text-warning': isWarn(user.books_count, user.novel_limit)
                    }">
                        {{$filters.numberFormat(user.books_count)}}
                        /
                        {{$filters.numberFormat(user.novel_limit)}}冊
                    </div>
                    <div class="mr-2" :class="{
                        'text-muted': user.maxEpisode == 0,
                        'text-danger': isOver(user.maxEpisode, user.episode_limit),
                        'text-warning': isWarn(user.maxEpisode, user.episode_limit)
                    }">
                        {{$filters.numberFormat(user.maxEpisode)}}
                        /
                        {{$filters.numberFormat(user.episode_limit)}}話
                    </div>
                    <div :class="{
                        'text-muted': user.articles_count == 0,
                        'text-danger': isOver(user.articles_count, user.article_limit),
                        'text-warning': isWarn(user.articles_count, user.article_limit)
                    }">
                        {{$filters.numberFormat(user.articles_count)}}
                        /
                        {{$filters.numberFormat(user.article_limit)}}記事
                    </div>
                    <div :class="{
                        'text-muted': user.storage_used == 0,
                        'text-danger': isOver(user.storage_used, user.storage_limit),
                        'text-warning': isWarn(user.storage_used, user.storage_limit)
                    }">
                        {{ $filters.getByte(user.storage_used) }}
                        /
                        {{ $filters.getByte(user.storage_limit) }}
                    </div>
                </div>
                <div class="col-md-3 d-flex justify-content-between">
                    <div>
                        <div>
                            {{$filters.fromNow(user.created_at )}} に登録
                        </div>
                        <div>
                            <span v-if="!user.deleted_at">
                                {{$filters.fromNow(user.made_at )}} ({{$filters.dateFormat(user.made_at , 'YYYY-MM-DD')}}) にアクセス
                            </span>
                            <span v-else>
                                {{$filters.fromNow(user.deleted_at )}} ({{$filters.dateFormat(user.deleted_at , 'YYYY-MM-DD')}}) に退会済み
                            </span>
                        </div>
                        <div v-if="user.wall" :class="{
                            'text-muted': user.wallPosts == 0,
                            'text-danger': user.wallPosts > 0
                        }">
                            <i class="fas fa-table-tennis"></i>
                            {{user.wall.wall_name}}
                            <span v-if="user.wallPosts">
                                ({{ user.wallPosts }})
                            </span>
                            <span v-else>(0)</span>
                        </div>
                    </div>
                    <div>
                        <router-link :to="`/admin/user/${user.id}?page=${page}`" class="btn btn-outline-primary">
                            <i class="fas fa-search-plus"></i>
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.bluesky-icon {
    width: 16px;
    height: 16px;
}
.deleted {
    opacity: .4;
}
.user-icon {
    width: 32px;
    height: 32px;
    display: inline-block;
    border-radius: 50%;
}
.fs-big {
    font-size: 1.2rem;
}
.text-muted {
    opacity: .6;
}
.badge {
    &.badge-white {
        background-color: #f3f5f7;
        color: #222;
    }
    &.badge-black {
        background-color: #16181a;
        color: snow;
    }
    &.badge-zouge {
        background-color: #e9e4d4;
        color: #222;
    }
    &.badge-sakura {
        background-color: #fdeff2;
        color: #222;
    }
    &.badge-dreamy {
        background: linear-gradient(70deg, #d8eaff, #f3e1ff, #ffceda);
        color: #222;
    }
    &.badge-forest {
        background: linear-gradient(70deg, #feffd8, #eeffe1, #ceffeb);
        color: #222;
    }
    &.badge-cosmo {
        background: linear-gradient(70deg, #9db9d2, #b9bfe0, #bcb3d6);
        color: #222;
    }
    &.badge-metal {
        background: linear-gradient(70deg, #d2c49d, #dce0b9, #c9d6b3);
        color: #222;
    }
    &.badge-doom {
        background: linear-gradient(70deg, #2b5876, #4e4376);
        color: #eee;
    }
    &.badge-rome {
        background: linear-gradient(70deg, #720101, #010006);
        color: #eee;
    }
    &.badge-jupiter {
        background: linear-gradient(70deg, #482623, #412a14, #35390b);
        color: #eee;
    }
    &.badge-space {
        background: linear-gradient(70deg, #030210, #10333c);
        color: #eee;
    }
    &.badge-small { font-size: 1.0rem; }
    &.badge-medium { font-size: 1.1rem; }
    &.badge-large { font-size: 1.2rem; }
    &.badge-xlarge { font-size: 1.3rem; }
    &.badge-huge { font-size: 1.4rem; }
}
</style>

<script>
export default {
    props: {
        user: {
            required: true
        },
        page: {
            default: 1
        }
    },
    methods: {
        forPortalBackgrounds(backgrounds) {
            const bg = _.filter(backgrounds, {for_portal: 1});
            if (!bg) {
                return 0;
            }
            return bg.length;
        },
        forBookshelfBackgrounds(backgrounds) {
            const bg = _.filter(backgrounds, {for_portal: 0, book_id: null, section_id: null});
            if (!bg) {
                return 0;
            }
            return bg.length;
        },
        forBookBackgrounds(backgrounds) {
            const bg = _.filter(backgrounds, (o) => { return o.book_id; });
            if (!bg) {
                return 0;
            }
            return bg.length;
        },
        forEpisodeBackgrounds(backgrounds) {
            const bg = _.filter(backgrounds, (o) => { return o.section_id; });
            if (!bg) {
                return 0;
            }
            return bg.length;
        },
        isOver(curr, max) {
            if (max <= curr) {
                return true;
            }
            return false;
        },
        isWarn(curr, max) {
            if ((max * 0.9) <= curr) {
                return true;
            }
            return false;
        },
        authorUrl(u) {
            return "//" + u.subdomain + this.$store.state.base_domain;
        },
    }
}
</script>